import { useCallback, useMemo } from 'react'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import {
  ConfigConditionSchema,
  ConfigConditionsSchemas,
  SelectSchema,
} from './ConfigConditionSchemas'
import { useConfigConditionCheck } from './useConfigCondition'
import ConfigConditions from './ConfigConditions'

export const useGetConfigConditionValue = <T extends ConfigConditionSchema>(
  schemaKey: T
) => {
  const config = useRemoteConfig()
  const getValue = useCallback(
    (name: string) => {
      const condition = config?.configConditions?.[name]
      if (!condition) {
        return undefined
      }
      const schema = ConfigConditionsSchemas[schemaKey]
      const parsed = schema.safeParse(condition.value)
      if (!parsed.success) {
        return undefined
      }
      return parsed.data as SelectSchema<T>
    },
    [config, schemaKey]
  )

  return getValue
}

export const useConfigConditionValue = <T extends ConfigConditionSchema>(
  name: string | undefined,
  schemaKey: T
) => {
  const config = useRemoteConfig()

  const value = useMemo(() => {
    return ConfigConditions.value(name, schemaKey, config)
  }, [name, schemaKey, config])

  return value
}

export const useValidConditionValue = <T extends ConfigConditionSchema>(
  name: string | undefined,
  schemaKey: T
) => {
  const value = useConfigConditionValue(name, schemaKey)
  const check = useConfigConditionCheck()

  const valid = useMemo(() => {
    if (!name) {
      return false
    }
    return check(name)
  }, [name, check])

  return valid ? value : undefined
}
